import type { Board, Kind, RNG } from "./types";

/** 从棋盘自身推断宽高（棋盘按 board[y][x] 索引：行数即高，列数即宽）。 */
export function boardDims(board: Board): { w: number; h: number } {
  const h = board.length;
  return { w: h > 0 ? board[0].length : 0, h };
}

/** 深拷贝棋盘。 */
export function cloneBoard(board: Board): Board {
  return board.map((row) => [...row]);
}

/** 原地 Fisher–Yates 洗牌，使用可注入的随机源。 */
function shuffleInPlace<T>(arr: T[], rng: RNG): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

// 生成一个初始棋盘：按 fillRate 填入成对的方块（保证每种 Kind 出现偶数次，
// 从而理论上可以两两消完）。注意这里不保证「当前布局有解」——调用方会用
// hasAnyValidMove 检测死局并在需要时重排。
// width×height 为棋盘尺寸（可为矩形，适配手机竖屏）；
// filledCount 为要填充的格子数（调用方保证，内部再夹一层：偶数、不超过总格数）。
export function generateBoard(
  width: number,
  height: number,
  typeCount: number,
  filledCount: number,
  rng: RNG = Math.random,
): Board {
  const board: Board = Array.from({ length: height }, () => Array<Kind | null>(width).fill(null));

  const total = width * height;
  let filled = Math.max(0, Math.min(total, Math.floor(filledCount)));
  if (filled % 2 !== 0) filled--; // 必须成对才能两两消完

  // 用前 typeCount 种 Kind，循环构造成对的方块池
  const pool: Kind[] = [];
  for (let i = 0; i < filled / 2; i++) {
    const kind = i % typeCount;
    pool.push(kind, kind);
  }
  shuffleInPlace(pool, rng);

  const positions: Array<{ x: number; y: number }> = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) positions.push({ x, y });
  }
  shuffleInPlace(positions, rng);

  for (let i = 0; i < pool.length; i++) {
    const pos = positions[i];
    board[pos.y][pos.x] = pool[i];
  }
  return board;
}

// 保持每个非空格子的「位置」不变，只把上面的 Kind 重新随机分配。
// 用于死局时「重整旗鼓」。返回新棋盘（尺寸由棋盘自身推断，兼容矩形）。
export function shuffleBoard(board: Board, rng: RNG = Math.random): Board {
  const next = cloneBoard(board);
  const { w, h } = boardDims(next);
  const kinds: Kind[] = [];
  const positions: Array<{ x: number; y: number }> = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (next[y][x] !== null) {
        kinds.push(next[y][x] as Kind);
        positions.push({ x, y });
      }
    }
  }
  shuffleInPlace(kinds, rng);
  positions.forEach((pos, idx) => {
    next[pos.y][pos.x] = kinds[idx];
  });
  return next;
}

/** 棋盘是否已清空（通关判定）。 */
export function isBoardEmpty(board: Board): boolean {
  return board.every((row) => row.every((cell) => cell === null));
}
